import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { Loader2 } from 'lucide-react';

function CategoriesSubTab({
  onSelect,
  selectedCat,
}: {
  onSelect: (cat: any) => void;
  selectedCat?: any;
}) {
  const { sellerId } = useUIStore();
  const [nodes, setNodes] = useState<Record<string, any>>({});
  const [roots, setRoots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchRes, setSearchRes] = useState<any[] | null>(null);
  const [tokens, setTokens] = useState<number | null>(null);

  const loadRoots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const cats = Array.isArray(res.data) ? res.data : res.data.categories || [];
        setTokens(res.data.tokens_left ?? null);
        const nm: Record<string, any> = {};
        const ri: string[] = [];
        cats.forEach((c: any) => {
          const id = String(c.catId || c.id || c.category_id);
          nm[id] = { data: c, children: [], expanded: false, loading: false, loaded: false };
          ri.push(id);
        });
        setNodes(nm);
        setRoots(ri);
        setSearchRes(null);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  const loadChildren = async (catId: string) => {
    setNodes(prev => ({ ...prev, [catId]: { ...prev[catId], loading: true } }));
    try {
      const res = await api.get<any>(`/publisher/discover/categories/${catId}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const children = Array.isArray(res.data) ? res.data : res.data.children || [];
        setTokens(res.data.tokens_left ?? null);
        setNodes(prev => {
          const u = { ...prev };
          const cids: string[] = [];
          children.forEach((c: any) => {
            const id = String(c.catId || c.id || c.category_id);
            if (!u[id]) u[id] = { data: c, children: [], expanded: false, loading: false, loaded: false };
            else u[id] = { ...u[id], data: c };
            cids.push(id);
          });
          u[catId] = { ...u[catId], children: cids, expanded: true, loading: false, loaded: true };
          return u;
        });
      }
    } catch {
      setNodes(prev => ({ ...prev, [catId]: { ...prev[catId], loading: false } }));
    }
  };

  const toggleNode = (catId: string) => {
    const n = nodes[catId];
    if (!n) return;
    if (n.expanded) setNodes(prev => ({ ...prev, [catId]: { ...prev[catId], expanded: false } }));
    else if (n.loaded) setNodes(prev => ({ ...prev, [catId]: { ...prev[catId], expanded: true } }));
    else loadChildren(catId);
  };

  const doSearch = async () => {
    if (!search.trim()) { setSearchRes(null); return; }
    setLoading(true);
    try {
      // Use roots and filter client-side, or call a search endpoint if available
      const res = await api.get<any>(`/publisher/discover/categories/roots?seller_id=${sellerId}`);
      if (res.success && res.data) {
        const cats = Array.isArray(res.data) ? res.data : res.data.categories || [];
        setTokens(res.data.tokens_left ?? null);
        setSearchRes(cats.filter((c: any) =>
          (c.name || '').toLowerCase().includes(search.trim().toLowerCase())
        ).slice(0, 30));
      }
    } catch {
      setSearchRes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRoots(); }, [loadRoots]);

  const renderNode = (catId: string, depth: number): React.ReactNode => {
    const n = nodes[catId];
    if (!n) return null;
    const c = n.data;
    const hasKids = (c.childrenCount || c.children_count || 0) > 0;
    const isSel = selectedCat && String(selectedCat.catId || selectedCat.id || selectedCat.category_id) === catId;

    return (
      <div key={catId}>
        <div
          className={`flex items-center gap-1.5 py-2 px-3 border-b transition-colors ${
            isSel ? 'bg-amber-600/10 border-amber-600/20' : 'border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]'
          }`}
          style={{ paddingLeft: `${12 + depth * 20}px` }}
        >
          {hasKids ? (
            <button
              onClick={() => toggleNode(catId)}
              className={`text-[10px] w-5 text-center flex-shrink-0 transition-transform ${n.expanded ? 'text-blue-400 rotate-90' : 'text-[var(--color-text-tertiary)]'}`}
            >
              {n.loading ? <Loader2 size={10} className="animate-spin" /> : '\u25B6'}
            </button>
          ) : (
            <span className="w-5 text-center flex-shrink-0 text-[var(--color-text-tertiary)] text-[6px]">{'\u25CF'}</span>
          )}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onSelect(c)}>
            <div className={`text-xs truncate ${isSel ? 'text-amber-400 font-semibold' : 'text-[var(--color-text)]'}`}>
              {c.name || c.category_name}
            </div>
            <div className="text-[10px] text-[var(--color-text-tertiary)]">
              {(c.productCount || 0).toLocaleString()} products
              {hasKids ? ` · ${c.childrenCount || c.children_count} sub` : ''}
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); onSelect(c); }}
            className={`flex-shrink-0 px-2.5 py-1 rounded text-[10px] font-medium ${
              isSel
                ? 'bg-amber-600/30 text-amber-400 border border-amber-500/40'
                : 'bg-amber-600/20 text-amber-500 border border-amber-600/30 hover:bg-amber-600/30'
            }`}
          >
            Select
          </button>
        </div>
        {n.expanded && n.children.length > 0 && (
          <div>{n.children.map((cid: string) => renderNode(cid, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); if (!e.target.value) setSearchRes(null); }}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          placeholder="Search Amazon categories..."
          className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        />
        <button
          onClick={doSearch}
          disabled={loading || !search.trim()}
          className="px-4 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
        >
          Search
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto rounded-xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
        {loading && roots.length === 0 ? (
          <div className="py-12 text-center text-[var(--color-text-tertiary)] text-xs">Loading categories...</div>
        ) : searchRes !== null ? (
          <div>
            <div className="flex justify-between items-center px-3 py-2 bg-amber-900/10 border-b border-amber-700/20 text-xs text-amber-400">
              <span>{'\u{1F50D}'} {searchRes.length} results for &quot;{search}&quot;</span>
              <button onClick={() => setSearchRes(null)} className="text-amber-400 hover:text-amber-300 underline">{'\u2715'} Clear</button>
            </div>
            {searchRes.map(cat => {
              const id = cat.catId || cat.id || cat.category_id;
              return (
                <div
                  key={id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer"
                  onClick={() => onSelect(cat)}
                >
                  <div className="flex-1">
                    <div className="text-xs text-[var(--color-text)]">{cat.name || cat.category_name}</div>
                    <div className="text-[10px] text-[var(--color-text-tertiary)]">
                      {(cat.productCount || 0).toLocaleString()} products
                      {(cat.childrenCount || cat.children_count || 0) > 0 ? ` · ${cat.childrenCount || cat.children_count} sub` : ''}
                      <span className="ml-1 opacity-60">ID: {id}</span>
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); onSelect(cat); }}
                    className="flex-shrink-0 px-2.5 py-1 bg-amber-600/20 text-amber-500 border border-amber-600/30 rounded text-[10px] font-medium hover:bg-amber-600/30"
                  >
                    Select
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          roots.map(cid => renderNode(cid, 0))
        )}
      </div>

      {tokens != null && (
        <div className="flex justify-end">
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${tokens > 100 ? 'bg-green-900/20 text-green-400' : tokens > 20 ? 'bg-yellow-900/20 text-yellow-400' : 'bg-red-900/20 text-red-400'}`}>
            {tokens} tokens left
          </span>
        </div>
      )}
    </div>
  );
}

export { CategoriesSubTab };
